/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useContext, FC, ReactNode, useState, useCallback, useEffect, useRef } from 'react';
import { GridPaginationModel } from '@mui/x-data-grid';
import { debounce } from '@mui/material/utils';

import { PaginationMeta, PaginationLinks, Pagination } from '@libs/dto';
import { useSnackbar } from '@carcareers/contexts';

interface DataGridContextProps<T> {
  items?: T[];
  meta?: PaginationMeta;
  links?: PaginationLinks;
  loading: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  rowCount: number;
  fetchData: (params?: Record<string, any>) => Promise<void>;
  refresh: () => Promise<void>;
  searchData: (params?: DataGridQueryParams) => Promise<void>;
  currentSearchTerm: string;
  isSearchMode: boolean;
}

const DataGridContext = createContext<DataGridContextProps<any>>({
  items: [],
  loading: false,
  paginationModel: { page: 0, pageSize: 10 },
  onPaginationModelChange: () => {
    throw new Error('useDataGrid must be used within a DataGridProvider');
  },
  rowCount: 0,
  fetchData: async () => {
    throw new Error('useDataGrid must be used within a DataGridProvider');
  },
  refresh: async () => {
    throw new Error('useDataGrid must be used within a DataGridProvider');
  },
  searchData: async () => {
    throw new Error('useDataGrid must be used within a DataGridProvider');
  },
  currentSearchTerm: '',
  isSearchMode: false,
} as DataGridContextProps<any>);

export interface DataGridQueryParams {
  q: string;
  id?: number;
  page?: number;
  limit?: number;
}

interface DataGridResponse<T> {
  data?: Pagination<T>;
  error?: any;
}

interface DataGridProviderProps<T> {
  index: (params: Omit<DataGridQueryParams, 'q'>) => Promise<DataGridResponse<T>>;
  search?: (params: DataGridQueryParams) => Promise<DataGridResponse<T>>;
  items?: T[];
  meta?: PaginationMeta;
  links?: PaginationLinks;
  children?: ReactNode;
  defaultParams?: Record<string, any>;
  subscription?: { data?: any; refetch?: () => void };
  onInit?: (refresh: () => Promise<void>) => void;
  id?: number;
}

export const DataGridProvider: FC<DataGridProviderProps<any>> = ({
  index,
  search,
  children,
  items: initialItems = [],
  meta: initialMeta,
  links: initialLinks,
  subscription,
  onInit,
  id,
}) => {
  const defaultPaginationModel = { page: 0, pageSize: 10 };
  const { toggleSnackbar } = useSnackbar();
  const [items, setItems] = useState<any[]>(initialItems);
  const [meta, setMeta] = useState<PaginationMeta | undefined>(initialMeta);
  const [links, setLinks] = useState<PaginationLinks | undefined>(initialLinks);
  const [loading, setLoading] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(defaultPaginationModel);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>('');
  const [isSearchMode, setIsSearchMode] = useState<boolean>(false);
  const isInitializedRef = useRef<boolean>(false);

  const fetchData = useCallback(
    async (params: Omit<DataGridQueryParams, 'q'> = {}) => {
      try {
        setLoading(true);

        const queryParams = {
          id,
          page: paginationModel.page + 1,
          limit: paginationModel.pageSize,
          ...params,
        };

        const response = await index(queryParams);

        if (response.data) {
          setItems(response.data.items);
          setMeta(response.data.meta);
          setLinks(response.data.links);
          isInitializedRef.current = true;
        } else if (response.error) {
          toggleSnackbar('error', 'Error fetching data.');
        }
      } catch (error) {
        toggleSnackbar('error', 'Error fetching data.');
      } finally {
        setLoading(false);
      }
    },
    [index, toggleSnackbar, paginationModel, id],
  );

  const searchDataInternal = useCallback(
    async (params?: DataGridQueryParams, isFromPagination = false) => {
      if (!search) return;

      try {
        setLoading(true);

        // Update search state
        const searchTerm = params?.q || '';
        const isNewSearch = searchTerm !== currentSearchTerm && !isFromPagination;
        
        setCurrentSearchTerm(searchTerm);
        setIsSearchMode(searchTerm.trim() !== '');

        // Only reset pagination to first page when starting a NEW search from the search input
        // Don't reset when paginating through existing search results
        const searchPage = params?.page || 1;
        if (isNewSearch && searchTerm.trim() !== '') {
          setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
        }

        const response = await search({
          id,
          q: searchTerm,
          page: searchPage,
          limit: params?.limit || paginationModel.pageSize,
        });

        if (response.data) {
          setItems(response.data.items);
          setMeta(response.data.meta);
          setLinks(response.data.links);
          isInitializedRef.current = true;
        } else if (response.error) {
          toggleSnackbar('error', 'Error fetching data.');
        }
      } catch (error) {
        toggleSnackbar('error', 'Error fetching data.');
      } finally {
        setLoading(false);
      }
    },
    [search, toggleSnackbar, paginationModel, currentSearchTerm, id],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchData = useCallback(
    debounce((params?: DataGridQueryParams) => searchDataInternal(params, false), 500),
    [searchDataInternal],
  );

  const refresh = useCallback(() => {
    // Clear search mode when refreshing
    setCurrentSearchTerm('');
    setIsSearchMode(false);
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    return fetchData();
  }, [fetchData, paginationModel.pageSize]);

  useEffect(() => {
    if (onInit) {
      onInit(refresh);
    }
  }, [onInit, refresh]);

  const onPaginationModelChange = useCallback(
    (newModel: GridPaginationModel) => {
      // Update pagination model first
      setPaginationModel(newModel);
      
      // Make the appropriate request based on current mode
      if (isSearchMode && currentSearchTerm.trim() !== '') {
        // Use search endpoint with current search term
        searchDataInternal({ 
          q: currentSearchTerm, 
          page: newModel.page + 1, 
          limit: newModel.pageSize 
        }, true);
      } else {
        // Use index endpoint 
        (async () => {
          try {
            setLoading(true);
            const response = await index({
              id,
              page: newModel.page + 1,
              limit: newModel.pageSize,
            });

            if (response.data) {
              setItems(response.data.items);
              setMeta(response.data.meta);
              setLinks(response.data.links);
            } else if (response.error) {
              toggleSnackbar('error', 'Error fetching data.');
            }
          } catch (error) {
            toggleSnackbar('error', 'Error fetching data.');
          } finally {
            setLoading(false);
          }
        })();
      }
    },
    [index, searchDataInternal, isSearchMode, currentSearchTerm, id, toggleSnackbar],
  );

  useEffect(() => {
    if (subscription?.data !== undefined) {
      refresh();
    }
  }, [subscription?.data, refresh]);

  useEffect(() => {
    if (!isInitializedRef.current) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rowCount = meta?.totalItems || 0;

  return (
    <DataGridContext.Provider
      value={{
        items,
        meta,
        links,
        loading,
        rowCount,
        paginationModel,
        fetchData,
        onPaginationModelChange,
        refresh,
        searchData,
        currentSearchTerm,
        isSearchMode,
      }}
    >
      {children}
    </DataGridContext.Provider>
  );
};

export function useDataGrid<T>() {
  const context = useContext<DataGridContextProps<T>>(DataGridContext);

  if (context === undefined) {
    throw new Error('useDataGrid must be used within a DataGridProvider');
  }

  return context;
}
